import { useState } from 'react'
import { api } from '../lib/api'
import { ConfirmModal } from '../components/ui/Modal'

// One danger treatment for every irreversible action on this page
const DANGER_CARD = 'bg-white rounded-lg shadow-md p-6 border-2 border-red-200'
const DANGER_BUTTON = 'min-h-[44px] px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
const DANGER_CONFIRM = 'bg-red-700 hover:bg-red-800'

export function DataOptionsPage() {
  const [showClearOrdersConfirm, setShowClearOrdersConfirm] = useState(false)
  const [showResetSlotsConfirm, setShowResetSlotsConfirm] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  async function handleClearOrders() {
    try {
      setLoading(true)
      setError(null)
      setSuccessMessage(null)

      const result = await api.clearAllOrders()

      setSuccessMessage(`Successfully deleted ${result.count} order(s)`)
      setShowClearOrdersConfirm(false)

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000)
    } catch (err) {
      setError('Failed to clear orders')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  async function handleResetTimeslots() {
    try {
      setLoading(true)
      setError(null)
      setSuccessMessage(null)

      const result = await api.resetAllTimeslots()

      setSuccessMessage(`Successfully reset ${result.count} timeslot(s) and deleted all Santa bookings`)
      setShowResetSlotsConfirm(false)

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000)
    } catch (err) {
      setError('Failed to reset timeslots')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Data Options</h1>

      {error && (
        <div role="alert" className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {successMessage && (
        <div role="status" className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {successMessage}
        </div>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* Clear All Orders Card */}
        <div className={DANGER_CARD}>
          <h2 className="text-lg font-semibold text-gray-800 mb-2">Clear All Orders</h2>
          <p className="text-sm text-gray-600 mb-4">
            This will permanently delete <strong>all orders</strong>, including their gifts and Santa bookings.
            This action cannot be undone.
          </p>

          <button
            onClick={() => setShowClearOrdersConfirm(true)}
            disabled={loading}
            className={DANGER_BUTTON}
          >
            Clear All Orders
          </button>
        </div>

        {/* Reset All Timeslots Card */}
        <div className={DANGER_CARD}>
          <h2 className="text-lg font-semibold text-gray-800 mb-2">Reset All Timeslots</h2>
          <p className="text-sm text-gray-600 mb-4">
            This will reset <strong>all timeslots</strong> to available and delete all Santa bookings.
            This action cannot be undone.
          </p>

          <button
            onClick={() => setShowResetSlotsConfirm(true)}
            disabled={loading}
            className={DANGER_BUTTON}
          >
            Reset All Timeslots
          </button>
        </div>
      </div>

      <ConfirmModal
        isOpen={showClearOrdersConfirm}
        onClose={() => setShowClearOrdersConfirm(false)}
        onConfirm={handleClearOrders}
        title="Confirm Clear All Orders"
        message={
          <p>
            Are you absolutely sure you want to delete <strong>all orders</strong>? This will permanently remove all orders,
            gifts, and Santa bookings from the database. This action cannot be undone.
          </p>
        }
        confirmText="Yes, Delete All Orders"
        confirmButtonClass={DANGER_CONFIRM}
        loading={loading}
      />

      <ConfirmModal
        isOpen={showResetSlotsConfirm}
        onClose={() => setShowResetSlotsConfirm(false)}
        onConfirm={handleResetTimeslots}
        title="Confirm Reset All Timeslots"
        message={
          <p>
            Are you absolutely sure you want to reset <strong>all timeslots</strong>? This will mark all timeslots
            as available and delete all Santa bookings. This action cannot be undone.
          </p>
        }
        confirmText="Yes, Reset All Timeslots"
        confirmButtonClass={DANGER_CONFIRM}
        loading={loading}
      />
    </div>
  )
}
